import { useCallback, useState } from 'react';
import { Header } from '../components/Header';
import { TabBar } from '../components/TabBar';
import * as api from '../api/client';
import type { SearchHit } from '../api/client';
import { SymbolSearch } from '../components/SymbolSearch';
import { newId } from '../lib/id';
import { price as fmtPrice, pctSigned, shortDate, todayIso } from '../lib/format';
import { useStore } from '../state/store';
import type { Quote, Watch } from '../types';
import type { Nav } from '../App';

/* Companies followed but not owned.
 *
 * They are kept out of the book proper on purpose. A position has a weight, a
 * ceiling and a thesis the rule engine can hold it to; a name on the radar has
 * none of those, and giving it a row among the holdings would put something in
 * the totals that no money is behind. What it does share is the market data —
 * a mark, a reporting date, and the same eight quarters, one tap away. */
/* What you are waiting to see, after you have started waiting.
 *
 * The reason for watching a company is the part that changes — it is written
 * before the thing happens and read after, and in between the thing usually
 * turns out to be slightly different. The symbol is not editable here on
 * purpose: it came from the search rather than from typing, so it is already
 * the exact string the upstream answers to, and changing it would mean
 * searching again, which is what removing and re-adding already is. */
function WatchNote({ watch }: { watch: Watch }) {
  const { putWatch } = useStore();
  const [open, setOpen] = useState(false);
  const [why, setWhy] = useState(watch.why ?? '');
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      const next: Watch = { ...watch };
      // Blank clears it. An honest empty note beats one kept because a form
      // would not let go of it.
      if (why.trim()) next.why = why.trim();
      else delete next.why;
      await putWatch(next);
      setOpen(false);
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <button className="btn btn-ghost btn-sm" style={{ marginTop: 8 }}
              onClick={() => { setWhy(watch.why ?? ''); setOpen(true); }}>
        {watch.why ? 'Edit what you are waiting for' : 'Say what you are waiting for'}
      </button>
    );
  }

  return (
    <div className="field" style={{ marginTop: 10 }}>
      <label htmlFor={`watch-why-${watch.id}`}>What you are waiting to see</label>
      <textarea
        id={`watch-why-${watch.id}`} className="input" rows={3}
        placeholder="leave it blank rather than filling it in to get past the form"
        value={why} onChange={(e) => setWhy(e.target.value)}
      />
      <div className="fab-row" style={{ marginTop: 8 }}>
        <button className="btn btn-block" onClick={() => setOpen(false)}>Cancel</button>
        <button className="btn btn-primary btn-block" disabled={busy} onClick={() => void save()}>
          {busy ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  );
}

export function Radar({ nav }: { nav: Nav }) {
  const { state, putWatch, removeWatch, cacheQuotes } = useStore();
  const watchlist = state.data.watchlist;

  const [symbol, setSymbol] = useState('');
  const [name, setName] = useState('');
  const [why, setWhy] = useState('');
  const [resolved, setResolved] = useState<Quote | null>(null);
  const [resolving, setResolving] = useState(false);
  const [note, setNote] = useState<string | null>(null);

  const quoteFor = (s: string) => state.quotes.find((q) => q.symbol === s) ?? null;

  /* The symbol comes from the search rather than from typing, so it is already
     the exact string the upstream answers to — §3.2's rule, satisfied by never
     having a guess in the first place. The quote is fetched for the mark and to
     confirm the listing still prices. */
  const pick = useCallback(async (hit: SearchHit) => {
    setSymbol(hit.symbol);
    setName(hit.name);
    setResolving(true);
    setNote(null);
    setResolved(null);
    try {
      const { rows } = await api.fetchQuotes([hit.symbol]);
      const q = rows[0];
      if (q) setResolved(q);
      else setNote(`${hit.symbol} did not price. It can still be added and will show no mark.`);
    } catch {
      setNote('Could not reach the price service. It can still be added and confirmed later.');
    } finally {
      setResolving(false);
    }
  }, []);

  const add = async () => {
    const typed = symbol.trim().toUpperCase();
    if (!typed) return setNote('Search for a company first.');
    const yahooSymbol = resolved?.symbol ?? typed;
    if (watchlist.some((w) => w.yahooSymbol === yahooSymbol)) {
      return setNote(`${yahooSymbol} is already on the radar.`);
    }

    const entry: Watch = {
      id: newId('watch'),
      ticker: resolved?.symbol ?? typed,
      yahooSymbol,
      addedAt: todayIso(),
    };
    if (why.trim()) entry.why = why.trim();
    if (resolved?.shortName ?? name) entry.name = resolved?.shortName ?? name;

    await putWatch(entry);
    // The mark that confirmed the symbol is the mark the card wants; keeping it
    // saves the card from reading "—" until the next refresh, which with the
    // market shut is tomorrow.
    if (resolved) await cacheQuotes([resolved]);
    setSymbol('');
    setName('');
    setWhy('');
    setResolved(null);
    setNote(resolved
      ? `${entry.ticker} added.`
      : `${entry.ticker} added, but the symbol was never confirmed, so it will show no mark until it resolves.`);
  };

  return (
    <div className="screen" data-tabbed="true">
      <Header onOpenSettings={() => nav.go({ name: 'settings' })} />

      <section>
        <div className="section-head">
          <h2>On the radar</h2>
          <span className="meta">{watchlist.length} watched · nothing owned</span>
        </div>

        {watchlist.length === 0 ? (
          <div className="empty">
            Nothing here yet. This is for companies you are following without holding.
            they take no weight, breach no limit, and never touch the book's total.
          </div>
        ) : (
          watchlist.map((w) => {
            const q = quoteFor(w.yahooSymbol);
            const change = q && q.previousClose > 0
              ? ((q.price - q.previousClose) / q.previousClose) * 100
              : null;
            return (
              <div className="card card-pad" key={w.id} style={{ marginBottom: 8 }}>
                <div className="kv">
                  {/* The ticker is the thing you scan the list for, so it is
                      given the weight of one — the row read as a grey murmur
                      when the name it is about was the quietest thing on it. */}
                  <span style={{ fontWeight: 600, fontSize: 15, color: 'var(--text)' }}>
                    {w.ticker}
                  </span>
                  <span className="v num" style={{ marginLeft: 'auto' }}>
                    {q ? fmtPrice(q.price, q.currency as never) : '—'}
                  </span>
                  {change === null ? null : (
                    <span className={`num ${change >= 0 ? 'up' : 'down'}`} style={{ marginLeft: 8 }}>
                      {change >= 0 ? '▲' : '▼'} {pctSigned(change)}
                    </span>
                  )}
                </div>

                {w.name ? (
                  <div style={{ fontSize: 12.5, color: 'var(--dim2)', marginTop: 2 }}>{w.name}</div>
                ) : null}
                {w.why ? (
                  <div style={{ fontSize: 13, marginTop: 8, lineHeight: 1.5, color: 'var(--text)' }}>
                    {w.why}
                  </div>
                ) : null}

                <WatchNote watch={w} />

                <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 10 }}>
                  <span style={{ fontSize: 11.5, color: 'var(--dim2)' }}>
                    watching since {shortDate(w.addedAt)}
                  </span>
                  <button
                    className="btn btn-ghost btn-sm"
                    style={{ marginLeft: 'auto' }}
                    onClick={() => nav.go({ name: 'earnings', symbol: w.yahooSymbol })}
                  >
                    See its results
                  </button>
                  <button
                    className="btn btn-ghost btn-sm"
                    onClick={() => void removeWatch(w.id)}
                    aria-label={`Stop watching ${w.ticker}`}
                  >
                    ×
                  </button>
                </div>
              </div>
            );
          })
        )}
      </section>

      <section>
        <div className="section-head"><h2>Follow something</h2></div>
        <div className="card card-pad">
          <SymbolSearch
            label="Symbol or name"
            placeholder="NVDA, Hesai, 2498.HK"
            onPick={(hit) => void pick(hit)}
          />

          {/* What you just found, said the way the results screen says it: the
              name in white and at size, not a grey line you have to hunt for. */}
          {resolved ? (
            <div style={{ marginBottom: 12 }}>
              <div className="subject-head" style={{ marginTop: 4 }}>
                <h2>{resolved.shortName ?? resolved.symbol}</h2>
                <span className="num">{fmtPrice(resolved.price, resolved.currency as never)}</span>
              </div>
              <div className="dim" style={{ fontSize: 11.5 }}>
                {resolved.symbol} · {resolved.exchangeName}
              </div>
            </div>
          ) : resolving ? (
            <div className="dim" style={{ fontSize: 12, marginBottom: 10 }}>Fetching its mark…</div>
          ) : null}

          <div className="field">
            <label htmlFor="watch-why">What are you waiting to see?</label>
            <textarea
              id="watch-why" className="input" rows={2}
              placeholder="Optional. The thing that would make this a position."
              value={why} onChange={(e) => setWhy(e.target.value)}
            />
          </div>

          <button className="btn btn-primary btn-block" onClick={() => void add()}>
            Add to the radar
          </button>

          {note ? (
            <div className="dim" style={{ fontSize: 11.5, marginTop: 10, lineHeight: 1.45 }}>
              {note}
            </div>
          ) : null}
        </div>
      </section>

      <TabBar current="radar" go={nav.go} />
    </div>
  );
}
